import React, { useMemo } from 'react';
import { Coordinates } from '../../types';

import { BRAND } from '../../constants/mapColors';
interface RouteOverviewMiniProps {
  routeCoords: Coordinates[];
  nearestIndex: number | null;
  userPosition: Coordinates | null;
  compact?: boolean;
  bottomOffsetPx?: number;
  isLandscape?: boolean;
}

const TILE_W = 140;
const TILE_H = 110;
const PAD = 12;

/**
 * Kompakt SVG-minimap som viser full rute med posisjonsprikk.
 */
const RouteOverviewMini: React.FC<RouteOverviewMiniProps> = ({
  routeCoords,
  nearestIndex,
  userPosition,
  compact = false,
  bottomOffsetPx,
  isLandscape = false,
}) => {
  const { drivenPath, remainingPath, userDot } = useMemo(() => {
    if (routeCoords.length < 2) return { drivenPath: '', remainingPath: '', userDot: null };

    // Beregn bounding box
    let minLat = Infinity, maxLat = -Infinity, minLon = Infinity, maxLon = -Infinity;
    for (const c of routeCoords) {
      if (c.latitude < minLat) minLat = c.latitude;
      if (c.latitude > maxLat) maxLat = c.latitude;
      if (c.longitude < minLon) minLon = c.longitude;
      if (c.longitude > maxLon) maxLon = c.longitude;
    }

    const latRange = maxLat - minLat || 0.001;
    const lonRange = maxLon - minLon || 0.001;
    const drawW = TILE_W - PAD * 2;
    const drawH = TILE_H - PAD * 2;

    // Projiser til SVG-koordinater (y invertert)
    const project = (c: Coordinates): [number, number] => {
      const x = PAD + ((c.longitude - minLon) / lonRange) * drawW;
      const y = PAD + ((maxLat - c.latitude) / latRange) * drawH;
      return [x, y];
    };

    // Forenkle ruten (ta hvert N-te punkt for ytelse)
    const step = Math.max(1, Math.floor(routeCoords.length / 200));
    const simplified: Coordinates[] = [];
    for (let i = 0; i < routeCoords.length; i += step) simplified.push(routeCoords[i]);
    if (simplified[simplified.length - 1] !== routeCoords[routeCoords.length - 1]) {
      simplified.push(routeCoords[routeCoords.length - 1]);
    }

    const splitIdx = nearestIndex != null ? Math.round(nearestIndex / step) : 0;
    const drivenCoords = simplified.slice(0, Math.min(splitIdx + 1, simplified.length));
    const remainCoords = simplified.slice(Math.max(0, splitIdx));

    const toPath = (coords: Coordinates[]) =>
      coords.map((c, i) => {
        const [x, y] = project(c);
        return `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`;
      }).join(' ');

    const driven = drivenCoords.length > 1 ? toPath(drivenCoords) : '';
    const remaining = remainCoords.length > 1 ? toPath(remainCoords) : toPath(simplified);

    // Brukerposisjon
    let dot: { x: number; y: number } | null = null;
    if (userPosition) {
      const [dx, dy] = project(userPosition);
      dot = { x: dx, y: dy };
    } else if (nearestIndex != null && routeCoords[nearestIndex]) {
      const [dx, dy] = project(routeCoords[nearestIndex]);
      dot = { x: dx, y: dy };
    }

    return { drivenPath: driven, remainingPath: remaining, userDot: dot };
  }, [routeCoords, nearestIndex, userPosition]);

  if (routeCoords.length < 2) return null;

  const displayW = compact ? Math.round(TILE_W * 0.5) : TILE_W;
  const displayH = compact ? Math.round(TILE_H * 0.5) : TILE_H;

  return (
    <div
      className="absolute z-mapOverlay rounded-xl shadow-lg border border-white/20 overflow-hidden backdrop-blur-sm"
      style={{
        bottom: bottomOffsetPx ?? (compact ? 56 : 100),
        ...(isLandscape || !compact ? { right: 16 } : { left: 8 }),
        width: displayW,
        height: displayH,
        background: 'rgba(15, 23, 42, 0.85)',
      }}
    >
      <svg width={displayW} height={displayH} viewBox={`0 0 ${TILE_W} ${TILE_H}`} preserveAspectRatio="xMidYMid meet">
        {/* Kjørt del (grå) */}
        {drivenPath && (
          <path d={drivenPath} fill="none" stroke="#6b7280" strokeWidth="2" strokeOpacity="0.5" strokeDasharray="3 2" />
        )}
        {/* Gjenstående del (emerald) */}
        {remainingPath && (
          <path d={remainingPath} fill="none" stroke={BRAND} strokeWidth="2.5" strokeOpacity="0.9" />
        )}
        {/* Brukerposisjon */}
        {userDot && (
          <>
            <circle cx={userDot.x} cy={userDot.y} r="6" fill={BRAND} opacity="0.25">
              <animate attributeName="r" values="4;8;4" dur="2s" repeatCount="indefinite" />
            </circle>
            <circle cx={userDot.x} cy={userDot.y} r="4" fill={BRAND} stroke="white" strokeWidth="1.5" />
          </>
        )}
      </svg>
    </div>
  );
};

export default RouteOverviewMini;
